import { z } from 'zod';

export const culturaEnum     = z.enum(['soja', 'milho']);
export const fonteEnum       = z.enum(['rta', 'nota_fiscal', 'romaneio', 'controle_interno', 'laudo_at', 'declaracao_produtor']);
export const tecnologiaEnum  = z.enum(['alta', 'ponta', 'media', 'baixa']);
export const cultivoEnum     = z.enum(['plantio_direto', 'convencional']);
export const maoDeObraEnum   = z.enum(['contratada', 'familiar']);
export const fertilidadeEnum = z.enum(['alta', 'media', 'baixa']);
export const agriculturaEnum = z.enum(['transgenica', 'tradicional']);
export const irrigacaoEnum   = z.enum(['sem_irrigacao', 'pivo_central', 'gotejamento']);
export const statusEnum      = z.enum(['pendente', 'confirmado', 'ajustar']);
export const tipoPosseEnum   = z.enum(['proprio', 'arrendamento']);

export const produtividadeSchema = z.object({
  id: z.coerce.number(),
  tenant_id: z.string().nullable().optional(),
  cliente_id: z.coerce.number(),

  imovel_id: z.coerce.number().nullable(),
  matricula: z.string().nullable(),
  lote: z.string().nullable(),
  gleba: z.string().nullable(),
  tipo_posse: tipoPosseEnum.nullable(),

  cultura: culturaEnum,
  safra: z.string(),
  sc_ha: z.coerce.number().nullable(),
  kg_ha: z.coerce.number().nullable(),

  fonte: fonteEnum.nullable(),
  tecnologia: tecnologiaEnum.nullable(),
  cultivo: cultivoEnum.nullable(),
  mao_de_obra: maoDeObraEnum.nullable(),
  fertilidade: fertilidadeEnum.nullable(),
  agricultura: agriculturaEnum.nullable(),
  irrigacao: irrigacaoEnum.nullable(),

  status: statusEnum,
  observacoes: z.string().nullable(),

  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),

  imovel_denominacao: z.string().nullable().optional(),
  imovel_matricula_ref: z.string().nullable().optional(),
});

export const produtividadeListSchema = z.array(produtividadeSchema);

export type Produtividade = z.infer<typeof produtividadeSchema>;

// Aviso suave (não bloqueia) quando cultura × safra foge da convenção:
// - Soja é safra de verão (Set/Jan→Jan/Abr): geralmente AAAA/AAAA+1.
// - Milho safrinha é 2ª safra (Fev/Mar→Jun/Ago): geralmente AAAA/AAAA.
// Casos atípicos (milho 1ª safra, soja extemporânea irrigada) são permitidos.
export function safraConventionWarning(cultura: string | null | undefined, safra: string | null | undefined): string | null {
  if (!cultura || !safra) return null;
  const m = safra.match(/^(\d{4})\/(\d{4})$/);
  if (!m) return null;
  const a = Number(m[1]);
  const b = Number(m[2]);
  if (cultura === 'soja' && b !== a + 1) {
    return `Atípico: soja normalmente é safra de verão (${a}/${a + 1}). Verifique se está correto.`;
  }
  if (cultura === 'milho' && b !== a) {
    return `Atípico: milho normalmente é safrinha (${b}/${b}). Se for milho 1ª safra, ignore este aviso.`;
  }
  return null;
}

// Input pra POST — id e timestamps virão do servidor. Sem refine de cultura×safra
// (regra é convenção, não dura — milho de 1ª safra existe em casos pontuais).
export const produtividadeCreateSchema = produtividadeSchema
  .omit({ id: true, tenant_id: true, created_at: true, updated_at: true, imovel_denominacao: true, imovel_matricula_ref: true })
  .partial({ status: true });
export type ProdutividadeCreateInput = z.infer<typeof produtividadeCreateSchema>;

// Input pra PATCH — qualquer campo opcional.
export const produtividadeUpdateSchema = produtividadeCreateSchema.partial();
export type ProdutividadeUpdateInput = z.infer<typeof produtividadeUpdateSchema>;
